import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation, Trans } from "react-i18next";
import { animate, onScroll } from "animejs";
import { Button } from "@/components/ui/button";
import { supabase } from "@/integrations/supabase/client";
import { Sentry } from "@/lib/sentry";
import { ArrowRight, ChevronRight } from "lucide-react";
import crIcon from "@/assets/cr-logo-icon-white.svg";
import { track } from "@/lib/analytics";
import { HeroQuickVerify } from "@/components/landing/HeroQuickVerify";
import { HeroTrustChips } from "@/components/landing/HeroTrustChips";
import { useTimeline } from "@/lib/anime/useTimeline";
import { DURATION, EASE } from "@/lib/anime/motion-vocab";
import { useHeroPerf } from "@/lib/anime/useHeroPerf";

// Globe canvas moved to HeroGlobeStage (sticky across hero + warp) so a
// single Three.js scene drives the whole entrance · no "second globe"
// spawning during the warp transition.

// Ambient-video gating now lives in `useHeroPerf().allowAmbientVideo`.
// That hook centralizes mobile / save-data / reduced-motion / hardware
// checks so every hero animation reads the same budget.

export function HeroSection() {
  const [stats, setStats] = useState({ status: "loading" });
  const perf = useHeroPerf();
  const { t } = useTranslation();

  // Refs for each animated element in the hero timeline
  const logoRef = useRef<HTMLDivElement>(null);
  const eyebrowRef = useRef<HTMLParagraphElement>(null);
  const headlineRef = useRef<HTMLDivElement>(null);
  const subtitleRef = useRef<HTMLParagraphElement>(null);
  const ctaRef = useRef<HTMLDivElement>(null);
  const priceRef = useRef<HTMLParagraphElement>(null);
  const previewRef = useRef<HTMLDivElement>(null);
  const statusRef = useRef<HTMLDivElement>(null);
  const heroGlowRef = useRef<HTMLDivElement>(null);
  // Section root + foreground-content wrapper · used as scroll-link targets.
  const sectionRef = useRef<HTMLElement>(null);
  const foregroundRef = useRef<HTMLDivElement>(null);

  // anime.js v4: top-level defaults live under `defaults`, not `ease`.
  const { build } = useTimeline({ defaults: { ease: EASE.soft } });

  useEffect(() => {
    supabase.functions
      .invoke("api-status")
      .then(({ data }) => {
        if (data) setStats({ status: data.status ?? "operational" });
      })
      .catch((err) => {
        setStats({ status: "operational" });
        Sentry.captureException(err instanceof Error ? err : new Error(String(err)), {
          level: "warning",
          tags: { source: "hero.api-status" },
        });
      });
  }, []);

  // Hero reveal timeline — orchestrates all elements in sequence.
  // Runs once on mount after refs are attached.
  useEffect(() => {
    const tl = build();
    if (!tl) {
      // Reduced motion: snap all elements to visible final state.
      [
        logoRef, eyebrowRef, headlineRef, subtitleRef, ctaRef, priceRef, previewRef, statusRef,
        heroGlowRef,
      ].forEach((r) => {
        if (r.current) {
          r.current.style.opacity = "1";
          r.current.style.transform = "none";
        }
      });
      return;
    }

    // 0. Hero glow: long, slow bloom underneath everything.
    if (heroGlowRef.current) {
      tl.add(heroGlowRef.current, {
        opacity: [0, 1],
        duration: DURATION.scene,
        ease: EASE.glide,
      }, 0);
    }

    // 1. Logo icon: scale + fade — start immediately so the mark is
    // visible on first paint instead of feeling delayed on mobile.
    if (logoRef.current) {
      tl.add(logoRef.current, {
        opacity: [0, 1],
        scale: [0.7, 1],
        duration: DURATION.base,
        ease: EASE.snap,
      }, 0);
    }
    // 2. Eyebrow: fade-up after logo
    if (eyebrowRef.current) {
      tl.add(eyebrowRef.current, {
        opacity: [0, 1],
        translateY: [12, 0],
        duration: DURATION.base,
        ease: EASE.soft,
      }, `-=${DURATION.base}`);
    }
    // 3. Headline: fade-up, overlapping with eyebrow finish
    if (headlineRef.current) {
      tl.add(headlineRef.current, {
        opacity: [0, 1],
        translateY: [16, 0],
        duration: DURATION.slow,
        ease: EASE.soft,
      }, "-=380");
    }
    // 4. Subtitle: fade-up, overlapping
    if (subtitleRef.current) {
      tl.add(subtitleRef.current, {
        opacity: [0, 1],
        translateY: [16, 0],
        duration: DURATION.base,
        ease: EASE.soft,
      }, "-=200");
    }
    // 5. CTA buttons: fade-up + slight scale
    if (ctaRef.current) {
      tl.add(ctaRef.current, {
        opacity: [0, 1],
        translateY: [12, 0],
        scale: [0.96, 1],
        duration: DURATION.base,
        ease: EASE.soft,
      }, "-=150");
    }
    // 6. Price anchor: fade-in
    if (priceRef.current) {
      tl.add(priceRef.current, {
        opacity: [0, 1],
        translateY: [8, 0],
        duration: DURATION.base,
        ease: EASE.soft,
      }, "-=100");
    }
    // 7. HeroQuickVerify wrapper: fade-up
    if (previewRef.current) {
      tl.add(previewRef.current, {
        opacity: [0, 1],
        translateY: [16, 0],
        duration: DURATION.base,
        ease: EASE.soft,
      }, "-=100");
    }
    // 8. Status bar: fade-up last
    if (statusRef.current) {
      tl.add(statusRef.current, {
        opacity: [0, 1],
        translateY: [8, 0],
        duration: DURATION.base,
        ease: EASE.soft,
      }, "-=100");
    }
  }, [build]);

  // Scroll-linked dissolve · as the user scrolls past the hero, the
  // foreground copy parallaxes up + fades and the glow contracts. Drives
  // the playhead via anime.js v4 `onScroll({ sync })` so it's perfectly
  // tied to scroll position. Gated by perf budget.
  useEffect(() => {
    if (!perf.allowScrollParallax) return;
    const section = sectionRef.current;
    const fg = foregroundRef.current;
    if (!section || !fg) return;

    const fgScroll = animate(fg, {
      translateY: [0, -120],
      scale: [1, 0.92],
      opacity: [1, 0],
      ease: "linear",
      autoplay: onScroll({
        target: section,
        enter: "top top",
        leave: "bottom top",
        sync: 0.6,
      }),
    });

    const glow = heroGlowRef.current
      ? animate(heroGlowRef.current, {
          opacity: [1, 0],
          scale: [1, 1.15],
          ease: "linear",
          autoplay: onScroll({
            target: section,
            enter: "top top",
            leave: "bottom top",
            sync: 0.5,
          }),
        })
      : null;

    return () => {
      fgScroll.pause();
      glow?.pause();
    };
  }, [perf.allowScrollParallax]);

  return (
    <section
      ref={sectionRef}
      className="relative min-h-[70svh] sm:min-h-[85svh] flex items-center justify-center overflow-hidden"
    >
      {/* Hero glow · soft radial bloom under everything, drives the entrance. */}
      <div ref={heroGlowRef} className="hero-glow absolute inset-0 pointer-events-none opacity-0" />
      {/* Bottom fade · lets the foreground copy sit cleanly on the backdrop. */}
      <div className={`absolute inset-0 pointer-events-none ${perf.tier === "lowPower" ? "bg-gradient-to-b from-transparent to-background/50" : "bg-gradient-to-b from-transparent via-background/40 to-background"}`} />

      {/* Globe canvas now lives in HeroGlobeStage (parent), which is sticky
       * across hero + warp. Trust chips remain as DOM overlay. */}
      {perf.allowWireframeScene && <HeroTrustChips />}

      <div ref={foregroundRef} className="relative z-10 container mx-auto px-4 sm:px-6 lg:px-8 text-center pt-28 sm:pt-36 pb-20 sm:pb-28 will-change-transform">

        {/* Logo with glow ring */}
        <div className="mb-8 sm:mb-10">
          <div
            ref={logoRef}
            style={{ opacity: 0 }}
            className="relative inline-block"
          >
            <div className="absolute inset-0 scale-150 bg-primary/10 rounded-full blur-3xl" />
            <img src={crIcon} alt="" width={80} height={80} loading="eager" decoding="sync" {...({ fetchpriority: "high" } as Record<string, string>)} className="relative mx-auto object-contain drop-shadow-[0_0_40px_hsl(160_100%_45%/0.3)] w-[80px] h-[80px] sm:w-[120px] sm:h-[120px]" />
          </div>
          <p
            ref={eyebrowRef}
            style={{ opacity: 0 }}
            className="mt-4 text-[10px] sm:text-[11px] font-semibold tracking-[0.22em] uppercase text-primary/80"
          >
            FMCSA · CVOR · CTQ · NSC · bonds · one search
          </p>
        </div>

        {/* HEADLINE - gradient text, massive */}
        <div
          ref={headlineRef}
          style={{ opacity: 0 }}
        >
          <h1 className="font-display text-3xl sm:text-4xl md:text-6xl lg:text-7xl xl:text-[84px] leading-[1.05] max-w-5xl mx-auto tracking-tight mb-6">
            <span className="text-foreground whitespace-pre-line">{t("hero.headline")}</span>
            <br />
            <span className="text-primary">{t("hero.headlineSub")}</span>
          </h1>
        </div>

        {/* Subtitle */}
        <p
          ref={subtitleRef}
          style={{ opacity: 0 }}
          className="text-base sm:text-lg md:text-xl text-muted-foreground max-w-2xl mx-auto leading-relaxed font-light whitespace-pre-line"
        >
          {t("hero.subtitle")}
        </p>

        {/* Tagline — visible on all screen sizes, including mobile */}
        <p className="mt-5 text-xs sm:text-sm font-semibold tracking-widest text-primary/90 uppercase">
          {t("hero.tagline")}
        </p>

        {/* CTA Buttons */}
        <div
          ref={ctaRef}
          style={{ opacity: 0 }}
        >
          <div className="mt-10 sm:mt-12 flex flex-col sm:flex-row items-center gap-4 justify-center">
            <div className="hover:-translate-y-0.5 hover:scale-[1.04] active:scale-[0.97] transition-transform duration-200">
              <Button
                variant="mint"
                size="lg"
                className="text-base px-10 h-12 sm:h-14 w-full sm:w-auto font-bold shadow-[0_0_30px_hsl(160_100%_45%/0.25)] hover:shadow-[0_0_50px_hsl(160_100%_45%/0.4)] transition-shadow duration-300"
                asChild
              >
                <Link
                  to="/signup?role=dispatcher&vertical=freight"
                  onClick={() => track("cta_clicked", { cta: "hero_signup", role: "dispatcher" })}
                >
                  {t("hero.cta")}
                  <ArrowRight className="h-4 w-4 ml-2" />
                </Link>
              </Button>
            </div>
            <div className="hover:translate-x-1 transition-transform duration-200">
              <Link
                to="/brokers/signup"
                onClick={() => track("cta_clicked", { cta: "hero_broker_signup", role: "broker" })}
                className="inline-flex items-center gap-1.5 text-sm font-semibold text-foreground/70 hover:text-foreground transition-colors group"
              >
                Sign up as broker
                <ChevronRight className="h-4 w-4 group-hover:translate-x-0.5 transition-transform" />
              </Link>
            </div>
            <div className="hover:translate-x-1 transition-transform duration-200">
              <Link
                to="/book-demo"
                onClick={() => track("cta_clicked", { cta: "hero_book_demo" })}
                className="inline-flex items-center gap-1.5 text-sm font-semibold text-foreground/60 hover:text-foreground/80 transition-colors group"
              >
                {t("hero.bookDemo")}
                <ChevronRight className="h-4 w-4 group-hover:translate-x-0.5 transition-transform" />
              </Link>
            </div>
          </div>
        </div>

        {/* Price anchor — honest about state. Dispatcher tiers are beta
            until Stripe price IDs are wired (project_stripe_pricing_pending_may02).
            Brokers and carriers can purchase today. */}
        <p
          ref={priceRef}
          style={{ opacity: 0 }}
          className="mt-5 text-sm text-muted-foreground"
        >
          <span className="text-primary font-bold">Free for carriers</span>{" "}
          · Brokers from <span className="text-primary font-bold">$249/mo</span>{" "}
          · Dispatchers in beta ·{" "}
          <Link to="/pricing" className="text-primary/80 underline underline-offset-4 hover:text-primary transition-colors">
            {t("hero.seePlans")}
          </Link>
        </p>

        {/* Live verify input · proof of function under the headline.
            On submit, hits FMCSA via carrier-lookup-public and shows a
            compact result inline. Deep-links to TryItNowSection for the
            full breakdown (broker mode, CA, risk flags). */}
        <div
          ref={previewRef}
          style={{ opacity: 0 }}
        >
          <HeroQuickVerify />
        </div>

        {/* Status bar - glass */}
        <div
          ref={statusRef}
          style={{ opacity: 0 }}
        >
          <div className="mt-12 sm:mt-16 inline-flex items-center gap-3 sm:gap-4 rounded-full glass-card px-5 py-3 text-sm sm:animate-[glow-breathe_4s_ease-in-out_infinite]">
            <span className="flex items-center gap-2">
              <span className="h-2 w-2 rounded-full bg-primary pulse-green" />
              <span className="text-foreground font-semibold">
                {stats.status === "loading" ? t("hero.connecting") : t("hero.operational")}
              </span>
            </span>
            <span className="h-4 w-px bg-white/10" />
            <span className="text-muted-foreground">{t("hero.subVerification")}</span>
          </div>
        </div>

      </div>
    </section>
  );
}
